import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiSearchColumns,
  khachHangSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  mauSoHoaDonSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  vatTu1SearchColumns,
  vuViecSearchColumns
} from '@/constants';
import {
  AccountModel,
  BoPhan,
  ChiPhi,
  type HanThanhToan,
  type KhachHang,
  KheUoc,
  type MauSoHoaDon,
  Phi,
  VatTu,
  VuViec
} from '@/types/schemas';
import { CellFieldV2 as CellField, SearchFieldV2 as SearchField } from '@/components/custom/erp';
import type { DataTableColumn } from '@/components/custom/erp/data-table-v2';

const mauBCOptions = [
  { value: '3', label: '3. Hóa đơn giá trị gia tăng' },
  { value: '5', label: '5. Hóa đơn bán hàng thông thường' },
  { value: '4', label: '4. Hàng hóa dịch vụ mua không hóa đơn' }
];

const maTCOptions = [
  {
    label:
      '1. Hàng hóa, dịch vụ dùng riêng cho SXKD chịu thuế GTGT và sử dụng cho các hoạt động cung cấp hàng hoá, dịch vụ không kê khai, nộp thuế GTGT đủ điều kiện',
    value: '1'
  },
  { label: '2. Hàng hóa, dịch vụ dùng chung cho SXKD chịu thuế GTGT và không chịu thuế GTGT', value: '2' },
  { label: '3. Hàng hóa, dịch vụ dùng cho dự án đầu tư đủ điều kiện khấu trừ thuế', value: '3' },
  { label: '4. Hàng hóa, dịch vụ không đủ điều kiện khấu trừ', value: '4' },
  { label: '5. Hàng hóa, dịch vụ không phải tổng hợp trên tờ khai 01/GTGT', value: '5' }
];

export const getTableColumns = (
  taxRates: BanHangRecord[],
  onCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void
): DataTableColumn[] => [
  {
    field: 'so_ct0',
    headerName: 'Số hóa đơn',
    width: 150,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='so_ct0'
        type='text'
        value={params.row.so_ct0 || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_ct0', newValue)}
      />
    )
  },
  {
    field: 'so_ct2',
    headerName: 'Ký hiệu',
    width: 100,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='so_ct2'
        type='text'
        value={params.row.so_ct2 || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_ct2', newValue)}
      />
    )
  },
  {
    field: 'ngay_ct0',
    headerName: 'Ngày hóa đơn',
    width: 150,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='ngay_ct0'
        type='date'
        value={params.row.ngay_ct0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ngay_ct0', newValue)}
      />
    )
  },
  {
    field: 'ma_thue',
    headerName: 'Thuế suất',
    width: 250,
    renderCell: params => {
      const taxRateOptions = taxRates.map(item => ({
        value: item.ma_thue,
        label: `${item.ten_thue}`
      }));

      return (
        <CellField
          commitOnBlur
          name='ma_thue'
          type='select'
          value={params.row.ma_thue}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_thue', newValue)}
          options={taxRateOptions}
        />
      );
    }
  },
  {
    field: 'ma_mau_ct',
    headerName: 'Mẫu hóa đơn',
    width: 150,
    renderCell: params => (
      <SearchField<MauSoHoaDon>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.MAU_SO_HOA_DON}/`}
        searchColumns={mauSoHoaDonSearchColumns}
        dialogTitle='Danh mục mẫu chứng từ'
        value={params.row.ma_mau_ct_data?.ma_mau_so || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_mau_ct_data', row)}
      />
    )
  },
  {
    field: 'ma_mau_bc',
    headerName: 'Mẫu báo cáo',
    width: 150,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='ma_mau_bc'
        type='select'
        value={params.row.ma_mau_bc || mauBCOptions[0]?.value}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_mau_bc', newValue)}
        options={mauBCOptions}
      />
    )
  },
  {
    field: 'ma_tc_thue',
    headerName: 'Mã tính chất',
    width: 150,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='ma_tc_thue'
        type='select'
        value={params.row.ma_tc_thue || maTCOptions[0]?.value}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_tc_thue', newValue)}
        options={maTCOptions}
      />
    )
  },
  {
    field: 'ma_kh',
    headerName: 'Mã NCC',
    width: 180,
    renderCell: params => (
      <SearchField<KhachHang>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.NHA_CUNG_CAP}/`}
        searchColumns={khachHangSearchColumns}
        columnDisplay='customer_code'
        dialogTitle='Danh mục nhà cung cấp'
        value={params.row.ma_ncc_data?.customer_code || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_ncc_data', row)}
      />
    )
  },
  {
    field: 'ten_kh_thue',
    headerName: 'Tên nhà cung cấp',
    width: 200,
    renderCell: params => (
      <CellField name='ten_nha_cung_cap' type='text' value={params.row.ma_ncc_data?.customer_name || ''} />
    )
  },
  {
    field: 'dia_chi',
    headerName: 'Địa chỉ',
    width: 250,
    renderCell: params => <CellField name='dia_chi' type='text' value={params.row.ma_ncc_data?.address || ''} />
  },
  {
    field: 'ma_so_thue',
    headerName: 'Mã số thuế',
    width: 150,
    renderCell: params => <CellField name='ma_so_thue' type='text' value={params.row.ma_ncc_data?.tax_code || ''} />
  },
  {
    field: 'ten_vt_thue',
    headerName: 'Tên hàng hóa - dịch vụ',
    width: 250,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='ten_vt_thue'
        type='text'
        value={params.row.ten_vt_thue || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ten_vt_thue', newValue)}
      />
    )
  },
  {
    field: 't_tien_nt',
    headerName: 'Tiền hàng VND',
    width: 150,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='t_tien_nt'
        type='number'
        value={params.row.t_tien_nt || 0.0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 't_tien_nt', newValue)}
      />
    )
  },
  {
    field: 'tk_thue_no',
    headerName: 'Tk thuế',
    width: 150,
    renderCell: params => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_thue_no_data?.code || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'tk_thue_no_data', row)}
      />
    )
  },
  {
    field: 't_thue_nt',
    headerName: 'Thuế VND',
    width: 150,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='t_thue_nt'
        type='number'
        value={params.row.t_thue_nt || 0.0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 't_thue_nt', newValue)}
      />
    )
  },
  {
    field: 'ma_kh9',
    headerName: 'Cục thuế',
    width: 150,
    renderCell: params => (
      <SearchField<KhachHang>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
        searchColumns={khachHangSearchColumns}
        columnDisplay='customer_code'
        dialogTitle='Danh mục đối tượng'
        value={params.row.customer_code_data?.customer_code || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'customer_code_data', row)}
      />
    )
  },
  {
    field: 'ma_tt',
    headerName: 'Mã thanh toán',
    width: 180,
    renderCell: params => (
      <SearchField<HanThanhToan> // Assuming PaymentMethodModel
        type='text'
        searchEndpoint={`/${QUERY_KEYS.HAN_THANH_TOAN}/`} // Replace with your actual endpoint for payment methods
        searchColumns={[]} // You'll need to define this array
        columnDisplay='ma_tt'
        dialogTitle='Danh mục thanh toán'
        value={params.row.ma_tt_data?.ma_tt || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_tt_data', row)}
      />
    )
  },
  {
    field: 'ghi_chu',
    headerName: 'Ghi chú',
    width: 200,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='ghi_chu'
        type='text'
        value={params.row.ghi_chu || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ghi_chu', newValue)}
      />
    )
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 150,
    renderCell: params => (
      <SearchField<BoPhan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
        searchColumns={boPhanSearchColumns}
        columnDisplay='ma_bp'
        dialogTitle='Danh mục thanh toán'
        value={params.row.ma_bp_data?.ma_pb || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
      />
    )
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 150,
    renderCell: params => (
      <SearchField<VuViec>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
        searchColumns={vuViecSearchColumns}
        columnDisplay='ma_vu_viec'
        dialogTitle='Danh mục vụ việc'
        value={params.row.ma_vv_data?.ma_vv || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
      />
    )
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 150,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='ma_hd'
        type='text'
        value={params.row.ma_hd || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_hd', newValue)}
      />
    )
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 150,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='ma_dtt'
        type='text'
        value={params.row.ma_dtt || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_dtt', newValue)}
      />
    )
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: params => (
      <SearchField<KheUoc>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
        searchColumns={kheUocSearchColumns}
        columnDisplay='ma_ku'
        dialogTitle='Danh mục khế ước'
        value={params.row.ma_ku_data?.ma_ku || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
      />
    )
  },

  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: params => (
      <SearchField<Phi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.PHI}/`}
        searchColumns={phiSearchColumns}
        columnDisplay='ma_phi'
        dialogTitle='Danh mục phí'
        value={params.row.ma_phi_data?.ma_phi || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
      />
    )
  },

  {
    field: 'ma_sp',
    headerName: 'Mã sản phẩm',
    width: 120,
    renderCell: params => (
      <SearchField<VatTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTu1SearchColumns}
        columnDisplay='ma_vt'
        dialogTitle='Danh mục vật tư'
        value={params.row.ma_sp}
        onRowSelection={(row: BanHangRecord) => {
          onCellValueChange(params.row.uuid, 'ma_sp_data', row);
        }}
      />
    )
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 120,
    renderCell: params => (
      <SearchField
        type='text'
        searchEndpoint={`/${QUERY_KEYS.LENH_SAN_XUAT}/`}
        searchColumns={lenhSanXuatSearchColumns}
        columnDisplay='ma_lsx'
        dialogTitle='Danh mục lệnh sản xuất'
        value={params.row.ma_lsx}
        onRowSelection={(row: BanHangRecord) => {
          onCellValueChange(params.row.uuid, 'ma_lsx_data', row);
        }}
      />
    )
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 120,
    renderCell: params => (
      <SearchField<ChiPhi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.CHI_PHI}/`}
        searchColumns={chiPhiSearchColumns}
        columnDisplay='ma_cp'
        dialogTitle='Danh mục chi phí'
        value={params.row.ma_phi_data?.ma_cp || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_cp_data', row)}
      />
    )
  }
];
